// constants.js - Centralized constants for the VHS Catalog application

// ============================================================================
// TIMING CONSTANTS
// ============================================================================
export const TIMING = {
  FEATURED_CAROUSEL_INTERVAL: 5000, // Featured movie rotation (5 seconds)
  DEBOUNCE_DELAY: 300, // Input debouncing
  TOAST_DURATION: 3000, // Success/error message duration
  ANIMATION_DURATION: 200, // CSS transition duration
  POLLING_INTERVAL: 30000, // Data refresh interval
};

// ============================================================================
// FORM VALIDATION LIMITS
// ============================================================================
export const FORM_LIMITS = {
  TITLE_MAX_LENGTH: 100, // Movie titles, post titles
  DESCRIPTION_MAX_LENGTH: 1000, // Movie descriptions, post content
  NOTES_MAX_LENGTH: 500, // Personal notes, seller notes
  USERNAME_MIN_LENGTH: 3, // User registration
  USERNAME_MAX_LENGTH: 50,
  PASSWORD_MIN_LENGTH: 8, // Password requirements
  EMAIL_MAX_LENGTH: 255, // Email validation
  SEARCH_MIN_LENGTH: 2, // Minimum search query length
};

// ============================================================================
// VALIDATION RULES (For existing component compatibility)
// ============================================================================
export const VALIDATION_RULES = {
  PASSWORD_MAX_LENGTH: 100, // Maximum password length
  USERNAME_MIN_LENGTH: 3, // Minimum username length
  USERNAME_MAX_LENGTH: 30, // Maximum username length
  TITLE_MAX_LENGTH: 200, // Maximum title length
  DESCRIPTION_MAX_LENGTH: 1000, // Maximum description length
  BIO_MAX_LENGTH: 500, // Maximum bio length
  LOCATION_MAX_LENGTH: 100, // Maximum location length
  NOTES_MAX_LENGTH: 300, // Maximum notes length
  SEARCH_MAX_LENGTH: 200, // Maximum search query length
};

// ============================================================================
// STORAGE KEYS
// ============================================================================
export const STORAGE_KEYS = {
  VHS_APP_ACCESS: 'vhs_app_access', // Session storage key for app access
};

// ============================================================================
// MARKETPLACE CONSTANTS
// ============================================================================
export const MARKETPLACE = {
  CONDITIONS: ['Poor', 'Fair', 'Good', 'Very Good', 'Excellent', 'Near Mint'],
  MIN_PRICE: 0.01, // Minimum listing price
  MAX_PRICE: 999.99, // Maximum listing price
  COMMISSION_RATE: 0.05, // 5% marketplace commission
  SHIPPING_RATES: {
    STANDARD: 4.99,
    EXPEDITED: 9.99,
    PRIORITY: 14.99,
  },
};

// ============================================================================
// PAGINATION & DISPLAY
// ============================================================================
export const DISPLAY = {
  ITEMS_PER_PAGE: 20, // Default pagination
  ITEMS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
  MAX_FEATURED_ITEMS: 6, // Featured carousel items
  MAX_RECENT_ACTIVITY: 10, // Recent activity feed
  MAX_SEARCH_RESULTS: 100, // Search result limit
  THUMBNAIL_SIZE: 150, // Image thumbnail dimensions
  FULL_IMAGE_SIZE: 800, // Full-size image dimensions
};

// ============================================================================
// CSS CLASS CONSTANTS
// ============================================================================
export const CSS_CLASSES = {
  // Buttons
  BUTTON_PRIMARY:
    'px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  BUTTON_SECONDARY:
    'px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors',
  BUTTON_SUCCESS: 'px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors',
  BUTTON_WARNING:
    'px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors',
  BUTTON_DANGER: 'px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors',
  BUTTON_SMALL: 'px-3 py-1 text-sm rounded transition-colors',
  BUTTON_LARGE: 'px-6 py-3 text-lg rounded transition-colors',

  // Form Elements
  INPUT_STANDARD:
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-red-500 focus:outline-none',
  INPUT_ERROR:
    'w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-0 focus:border-red-500 focus:outline-none bg-red-50',
  TEXTAREA_STANDARD:
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-red-500 focus:outline-none resize-none',
  SELECT_STANDARD:
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-red-500 focus:outline-none',

  // Cards & Containers
  CARD_STANDARD: 'bg-white rounded-lg shadow-md border border-gray-200',
  CARD_HOVER:
    'bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer',
  CARD_PADDING: 'p-4',
  CARD_PADDING_LARGE: 'p-6',

  // Layout
  CONTAINER_MAIN: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  SPACING_STANDARD: 'space-y-4',
  SPACING_LARGE: 'space-y-6',
  FLEX_CENTER: 'flex items-center justify-center',
  FLEX_BETWEEN: 'flex items-center justify-between',

  // Text Styles
  HEADING_PRIMARY: 'text-2xl font-bold text-gray-900',
  HEADING_SECONDARY: 'text-lg font-semibold text-gray-900',
  TEXT_MUTED: 'text-gray-600',
  TEXT_SMALL: 'text-sm text-gray-600',
  TEXT_ERROR: 'text-sm text-red-600',
  TEXT_SUCCESS: 'text-sm text-green-600',

  // Status Indicators
  STATUS_SUCCESS: 'px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium',
  STATUS_WARNING: 'px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium',
  STATUS_ERROR: 'px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium',
  STATUS_INFO: 'px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium',

  // Navigation
  TAB_ACTIVE: 'border-red-500 text-red-600',
  TAB_INACTIVE: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
  TAB_BASE: 'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
};

// ============================================================================
// FORUM CATEGORIES
// ============================================================================
export const FORUM_CATEGORIES = [
  { value: 'general', label: 'General Discussion', description: 'General collecting talk' },
  { value: 'finds', label: 'Recent Finds', description: 'Share your awesome discoveries' },
  { value: 'tips', label: 'Tips & Advice', description: 'Help fellow collectors' },
  { value: 'trading', label: 'Trading Talk', description: 'Discuss trades and deals' },
  { value: 'restoration', label: 'Restoration Help', description: 'Technical repair advice' },
  {
    value: 'identification',
    label: 'Identification Help',
    description: 'Help identifying tapes',
  },
  { value: 'releases', label: 'Release Info', description: 'Different releases and variants' },
  { value: 'showcase', label: 'Collection Showcase', description: 'Show off your collection' },
];

// ============================================================================
// MOVIE GENRES
// ============================================================================
export const GENRES = {
  OPTIONS: [
    'Action',
    'Adventure',
    'Animation',
    'Comedy',
    'Crime',
    'Documentary',
    'Drama',
    'Family',
    'Fantasy',
    'Horror',
    'Music',
    'Mystery',
    'Romance',
    'Sci-Fi',
    'Thriller',
    'War',
    'Western',
  ],
  EMOJIS: {
    Horror: '🧟',
    Action: '💥',
    Comedy: '😂',
    Drama: '🎭',
    'Sci-Fi': '🚀',
    Thriller: '😱',
    Romance: '💕',
    Adventure: '🗺️',
    Animation: '🎨',
    Fantasy: '🧙',
    Crime: '🕵️',
    Documentary: '📹',
    Family: '👨‍👩‍👧‍👦',
    Music: '🎵',
    Mystery: '🔍',
    War: '⚔️',
    Western: '🤠',
  },
};

// ============================================================================
// ERROR MESSAGES
// ============================================================================
export const ERROR_MESSAGES = {
  NETWORK: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested item was not found.',
  VALIDATION: 'Please check your input and try again.',
  SERVER: 'Server error. Please try again later.',
  TIMEOUT: 'Request timed out. Please try again.',
  UNKNOWN: 'An unexpected error occurred. Please try again.',

  // Form specific
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  PASSWORD_TOO_SHORT: `Password must be at least 8 characters long.`,
  TITLE_TOO_LONG: `Title cannot exceed 100 characters.`,
  DESCRIPTION_TOO_LONG: `Description cannot exceed 1000 characters.`,

  // Collection specific
  ALREADY_IN_COLLECTION: 'This item is already in your collection.',
  COLLECTION_FULL: 'Your collection has reached the maximum limit.',

  // Marketplace specific
  INVALID_PRICE: 'Please enter a valid price.',
  ITEM_ALREADY_LISTED: 'This item is already listed for sale.',
  CANNOT_BUY_OWN_ITEM: 'You cannot purchase your own item.',
};

// ============================================================================
// SUCCESS MESSAGES
// ============================================================================
export const SUCCESS_MESSAGES = {
  ITEM_ADDED: 'Item successfully added!',
  ITEM_UPDATED: 'Item successfully updated!',
  ITEM_DELETED: 'Item successfully deleted!',
  COLLECTION_UPDATED: 'Collection updated successfully!',
  WANTLIST_UPDATED: 'Wantlist updated successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  MESSAGE_SENT: 'Message sent successfully!',
  LISTING_CREATED: 'Listing created successfully!',
  PURCHASE_COMPLETED: 'Purchase completed successfully!',
};

const constants = {
  TIMING,
  FORM_LIMITS,
  VALIDATION_RULES,
  STORAGE_KEYS,
  MARKETPLACE,
  DISPLAY,
  CSS_CLASSES,
  FORUM_CATEGORIES,
  GENRES,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
};

export default constants;
